/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
/* eslint-disable jsx-a11y/anchor-has-content */
import { FloatingPortal } from '@floating-ui/react'
import { Copy, EditPencil, Eye } from 'iconoir-react'
import { upperCase } from 'lodash'
import { useState } from 'react'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import { withZod } from '@remix-validated-form/with-zod'
import Button from 'app/components/Button'
import FloatingSave from 'app/components/FloatingSave'
import FormCombo from 'app/components/form/FormCombo'
import FormInput from 'app/components/form/FormInput'
import { SmartForm } from 'app/components/form/SmartForm'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/PageHeader'
import { SchedulingFormFieldsCard } from 'app/components/scheduling/SchedulingFormFieldsCard'
import { LocationLogoUploader } from 'app/components/scheduling/LocationLogoUploader'
import { SectionCard } from 'app/components/SectionCard'
import { Toggle } from 'app/components/Toggle'
import { LeadOverrideConfigType } from 'app/types/entities'
import { enforceMinMax } from 'lib/enforceMinMaxInput'
import { OptionType } from 'types/OptionType'
import type { UseOnlineSchedulerLocationReturn } from './useOnlineSchedulerLocation'

const leadOverrideSchema = z.object({
  status: z.enum([`enabled`, `disabled`]),
  amount: zfd.numeric(),
  unit: z.enum([`days`, `weeks`]),
})

const leadOverrideValidator = withZod(leadOverrideSchema) as any

function LeadOverridePanel({
  config,
  enabled,
  onSaved,
  onCancel,
}: {
  config?: LeadOverrideConfigType
  enabled?: boolean
  onSaved: (data: { enabled: boolean; config: LeadOverrideConfigType }) => void
  onCancel: () => void
}) {
  const [selectedStatus, setSelectedStatus] = useState<OptionType>(
    enabled
      ? { id: `enabled`, name: `Enabled`, value: `enabled` }
      : { id: `disabled`, name: `Disabled`, value: `disabled` },
  )
  const [selectedUnit, setSelectedUnit] = useState<OptionType | undefined>(
    config ? { id: config.unit, name: upperCase(config.unit), value: config.unit } : undefined,
  )

  return (
    <SmartForm
      className="!mt-0 !max-w-none !gap-y-0"
      validator={leadOverrideValidator}
      onSubmit={(data) =>
        onSaved({
          enabled: data.status === `enabled`,
          config: { amount: data.amount, unit: data.unit },
        })
      }
      preventSubmit
      hideButtons
    >
      <div className="col-span-6 flex flex-col gap-y-6 p-4">
        <FormCombo
          cols={6}
          name="status"
          label="Status"
          placeholder="Select status"
          options={[
            { id: `enabled`, name: `Enabled`, value: `enabled` },
            { id: `disabled`, name: `Disabled`, value: `disabled` },
          ]}
          value={selectedStatus}
          onChange={(option) => setSelectedStatus(option)}
          labelClassName="text-label-sm text-strong"
        />
        <FormInput
          name="amount"
          label="Amount"
          type="number"
          max={selectedUnit?.value === `days` ? 30 : 4}
          min={1}
          required={true}
          labelClassName="text-label-sm text-strong"
          defaultValue={config?.amount}
          cols={6}
          onChange={(e) => enforceMinMax(e)}
        />
        <FormCombo
          cols={6}
          name="unit"
          label="Units"
          placeholder="Select a unit"
          options={[
            { id: `days`, name: `Days`, value: `days` },
            { id: `weeks`, name: `Weeks`, value: `weeks` },
          ]}
          value={selectedUnit}
          onChange={(option) => setSelectedUnit(option)}
          labelClassName="text-label-sm text-strong"
        />
      </div>
      <div className="relative bottom-0 z-10 col-span-6 flex justify-end rounded-b-lg border-b border-t border-default bg-surface px-4 py-3">
        <div className="flex justify-end gap-4">
          <Button label="Cancel" mode="white" size="md" onClick={() => onCancel()} />
          <Button type="submit" label="Save" mode="primary" size="md" />
        </div>
      </div>
    </SmartForm>
  )
}

export type OnlineSchedulerLocationComponentProps = UseOnlineSchedulerLocationReturn

export function OnlineSchedulerLocationComponent(props: OnlineSchedulerLocationComponentProps) {
  const {
    location,
    schedulingConfiguration,
    customQuestions,
    showQuestionPanel,
    setShowQuestionPanel,
    showLeadOverride,
    setShowLeadOverride,
    isDirtyForm,
    locationId,
    floatingData,
    floatingDataLeadOverride,
    getReferenceProps,
    getFloatingProps,
    getLeadOverrideReferenceProps,
    getLeadOverrideFloatingProps,
    saveRequest,
    onToggleSchedulerLive,
    copyToClipboard,
    schedulingUrl,
    handleSaveConfiguration,
    onMoveQuestion,
    onDropQuestion,
    onQuestionCreateCompleted,
    onQuestionUpdateCompleted,
    onQuestionDeleted,
    onLeadOverrideSaved,
  } = props

  return (
    <>
      <PageScroll
        contentClassName="mt-4 flex flex-col gap-6 pb-9"
        header={
          <PageHeader
            title={location?.name ?? ``}
            secondaryButtonSlot={
              <div className="flex items-center gap-2">
                <button
                  className="btn btn-md btn-tertiary my-1"
                  onClick={() =>
                    copyToClipboard(
                      `${schedulingUrl}?preview=true`,
                      `Copied preview link to clipboard`,
                      `copy-preview-to-clipboard`,
                    )
                  }
                >
                  <Eye /> <p>Copy preview link</p>
                </button>
                <button
                  className="btn btn-md btn-tertiary my-1"
                  onClick={() => copyToClipboard(schedulingUrl, `Copied to clipboard`, `copy-to-clipboard`)}
                >
                  <Copy /> <p>Copy scheduling link</p>
                </button>
                <Toggle
                  defaultValue={location.isSchedulerLive}
                  label="Scheduler Live"
                  controlled
                  handleToggle={onToggleSchedulerLive}
                  labelClassName="text-strong text-body-sm"
                />
              </div>
            }
          />
        }
      >
        {/* General Settings - Lead Capture Override */}
        <SectionCard title="General Settings" className="max-w-[750px]">
          <div>
            <p className="mb-4 text-label-sm text-strong">Lead Capture Override</p>
            <button
              className="flex w-full items-center justify-between rounded-lg border border-default bg-surface px-3 py-2.5 text-strong"
              ref={floatingDataLeadOverride.refs.setReference}
              {...getLeadOverrideReferenceProps()}
            >
              <div className="flex items-center gap-2">
                <span className="text-label-sm">
                  {schedulingConfiguration?.leadOverrideEnabled ? `Enabled` : `Disabled`}
                </span>
                {schedulingConfiguration?.leadOverrideEnabled && schedulingConfiguration?.leadOverrideConfig && (
                  <>
                    <span className="text-minimal">•</span>
                    <span className="text-label-sm">{schedulingConfiguration?.leadOverrideConfig?.amount}</span>
                    <span className="text-label-sm">{schedulingConfiguration?.leadOverrideConfig?.unit}</span>
                  </>
                )}
              </div>
              <EditPencil height={20} />
            </button>
            {showLeadOverride && (
              <FloatingPortal>
                <div
                  className="rounded-lg border border-default bg-surface shadow-elevation-2"
                  ref={floatingDataLeadOverride.refs.setFloating}
                  {...getLeadOverrideFloatingProps()}
                  style={{ ...floatingDataLeadOverride.floatingStyles }}
                >
                  <LeadOverridePanel
                    config={schedulingConfiguration?.leadOverrideConfig}
                    enabled={schedulingConfiguration?.leadOverrideEnabled}
                    onSaved={onLeadOverrideSaved}
                    onCancel={() => setShowLeadOverride(false)}
                  />
                </div>
              </FloatingPortal>
            )}
          </div>
        </SectionCard>

        {/* Branding */}
        <SectionCard title="Branding" className="max-w-[750px]">
          <div>
            <p className="text-ui-md text-strong">Online Scheduling Logo</p>
            <p className="mb-6 mt-2 text-body-sm text-strong">
              This logo will be used in the online scheduler. If no logo is provided, the location name will be shown
              instead.
            </p>
            <LocationLogoUploader locationId={locationId!} />
            <p className="mt-4 text-body-xs text-minimal">Recommended Dimensions: 300x100 pixels</p>
          </div>
        </SectionCard>

        {/* Form Fields */}
        <SectionCard title="Form Fields" className="max-w-[750px]">
          <SchedulingFormFieldsCard {...props} />
        </SectionCard>
      </PageScroll>
      <FloatingSave isDirtyForm={isDirtyForm} handleSaveConfiguration={handleSaveConfiguration} />
    </>
  )
}
